import './createEditPolicyParameters.less'
import StepperV2 from '../../../../../components/StepperV2'
import { URL_POLICY_PARAMETERS } from '../../../../../constants'
import historyService from '../../../../../services/historyService'
import { getUpdatedUrl } from '../../../../../utils/urlUtil'
import { CreatePolicyParametersIcon } from '../../components/common/icon'
import { BasicDetails } from '../../components/createEditPolicyParams/basicDetails'
import { Detection } from '../../components/createEditPolicyParams/detection'
import { BASE_PATH } from '../../constants'
import PopupConfirm from '../../../../../components/PopupConfirm/popupConfirm'
import { usePolicyParametersContext } from '../../context/policyParametersContext'

import { useReduxSelector } from '../../../../../hooks/useReduxSelector'
import { getActionState, getShowLoader, resetAction } from '../../../../../reducers/requestReducer'
import {
  ACTION_POLICY_PARAMETERS_CREATE,
  ACTION_POLICY_PARAMETERS_FETCH_BY_ID,
  ACTION_POLICY_PARAMETERS_UPDATE,
  createPolicyParameters,
  fetchPolicyParameterById,
  resetSelectedPolicyParameter,
  updatePolicyParameters
} from '../../policySettingsSlice'
import { STATES } from '../../../../../interfaces'
import { addNotification } from '../../../../notification/notificationSlice'
import CustomLoader from '../../../../../components/Loader/loader'
import { useDispatch } from 'react-redux'
import { FormattedMessage, useIntl } from 'react-intl'
import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Col, Typography } from '@lightbeamai/design-system'

export const CreateEditPolicyParameters = () => {
  const intl = useIntl()
  const dispatch = useDispatch()

  const {
    isFormValid,
    basicDetails,
    detection,
    resetForm,
    mode,
    policyParamIdFromUrl
  } = usePolicyParametersContext()
  const [showConfirmSave, setShowConfirmSave] = useState(false)
  const actionCreatePolicyParameterState = useReduxSelector(() =>
    getActionState(ACTION_POLICY_PARAMETERS_CREATE)
  )
  const actionUpdatePolicyParameterState = useReduxSelector(() =>
    getActionState(ACTION_POLICY_PARAMETERS_UPDATE)
  )
  const { isView } = mode
  const showCreateLoader = useReduxSelector(
    () =>
      getShowLoader(ACTION_POLICY_PARAMETERS_CREATE) ||
      getShowLoader(ACTION_POLICY_PARAMETERS_UPDATE)
  )
  const showLoader = useReduxSelector(() => getShowLoader(ACTION_POLICY_PARAMETERS_FETCH_BY_ID))

  useEffect(
    function fetchPolicyParameter() {
      if (policyParamIdFromUrl) {
        dispatch(fetchPolicyParameterById(policyParamIdFromUrl))
      }
    },
    [policyParamIdFromUrl, dispatch]
  )

  const handleClose = useCallback(() => {
    historyService.push(
      getUpdatedUrl({
        pathName: BASE_PATH + URL_POLICY_PARAMETERS
      })
    )
    dispatch(resetSelectedPolicyParameter())
  }, [])

  useEffect(function resetFormOnUnmount() {
    return () => {
      resetForm()
      dispatch(resetSelectedPolicyParameter())
    }
  }, [])

  const handleSave = () => {
    if (isFormValid) {
      if (policyParamIdFromUrl) {
        dispatch(updatePolicyParameters({ basicDetails, detection, id: policyParamIdFromUrl }))
      } else {
        dispatch(createPolicyParameters({ basicDetails, detection }))
      }
    }
  }

  useEffect(() => {
    const state = actionCreatePolicyParameterState || actionUpdatePolicyParameterState
    if (state === STATES.failed || state === STATES.success) {
      dispatch(
        addNotification({
          success: state === STATES.success ? 'policy.settings.parameters.save.success' : '',
          error: state === STATES.failed ? 'policy.settings.parameters.save.error' : ''
        })
      )

      dispatch(resetAction(ACTION_POLICY_PARAMETERS_CREATE))
      dispatch(resetAction(ACTION_POLICY_PARAMETERS_UPDATE))
      if (state === STATES.success) {
        resetForm()
        setTimeout(() => {
          handleClose()
        }, 500)
      }
    }
  }, [
    actionCreatePolicyParameterState,
    actionUpdatePolicyParameterState,
    addNotification,
    resetAction
  ])

  const flowControlProps = {
    backButtonProps: {
      onClick: () => null,
      disabled: false,
      visible: false
    },
    nextButtonProps: {
      onClick: isView ? handleClose : () => setShowConfirmSave(true),
      label: isView
        ? intl.formatMessage({ id: 'btn.close' })
        : intl.formatMessage({ id: 'btn.save' }),
      disabled: isView ? false : !isFormValid
    },
    onCancel: handleClose,
    showCancelButton: !isView,
    confirmCancelContent: <>{intl.formatMessage({ id: 'confirmClose' })}</>
  }

  const actionLabelId = mode.isCreate ? 'create' : mode.isEdit ? 'edit' : 'view'
  const stepperProps = {
    title: (
      <div className="create-policy-params-title">
        <CreatePolicyParametersIcon />
        <Typography variant="h4">
          {intl.formatMessage({
            id: `policy.settings.paramerters.${actionLabelId}`
          })}
        </Typography>
      </div>
    ),
    steps: [],
    activeStep: 1,
    onClose: handleClose
  }

  return (
    <StepperV2
      isFullScreen
      showStepper={false}
      stepperProps={stepperProps}
      flowControlProps={flowControlProps}
      wide={true}
      showConfirmClose={!isView}
      className="create-policy-params"
    >
      <div className="create-policy-params-details-wrapper">
        <Col className="create-policy-params-details" span={9}>
          <BasicDetails />
          <Detection />
        </Col>
      </div>
      <PopupConfirm
        open={showConfirmSave}
        onConfirm={handleSave}
        onCancel={() => setShowConfirmSave(false)}
        className="policy-params-confirm"
        confirmButton={intl.formatMessage({ id: 'btn.save' })}
        header={<FormattedMessage id="btn.attributeSet.edit.text" />}
        onClose={() => setShowConfirmSave(false)}
        dismissible
        isLoading={showCreateLoader}
        content={
          <>
            <Typography type="medium" className="xs-mb-12">
              {intl.formatMessage({
                id: 'policy.settings.parameters.save.content_1'
              })}
            </Typography>
            <ul className="save-policy-param-list">
              <Typography as="li">
                {intl.formatMessage({
                  id: 'policy.settings.parameters.save.content_2'
                })}
              </Typography>
              <Typography as="li">
                {intl.formatMessage({
                  id: 'policy.settings.parameters.save.content_3'
                })}
              </Typography>
            </ul>
            <Alert
              type="info"
              className="key-value-alert xs-mt-16"
              showIcon
              message={intl.formatMessage({ id: 'policy.settings.parameters.save.notification' })}
            />
          </>
        }
      />
      <CustomLoader showLoader={showLoader} />
    </StepperV2>
  )
}
